import Backdrop from "@components/Backdrop"
import VerticalSpace from "@components/VerticalSpace"
import { cubicBezier, timings } from "@constants/animation"
import colors from "@constants/colors"
import { DatoResponsiveImage } from "@dato/types"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import { MobileOnly } from "@utils/MediaQueries"
import Link from "next/link"
import { useRouter } from "next/router"
import React from "react"
import { down, up } from "styled-breakpoints"
import styled, { css } from "styled-components"
import CarSelector from "./CarSelector"
import PowersportsCarSelector from "./PowersportsCarSelector"
import Footer from "./Footer"
import Header from "./Header"
import Item from "./Item"
import ProSeries from "./ProSeries"
import Products from "./Products"
import SubMenu from "./SubMenu"

const openProps = css`
  opacity: 1;
  transform: translateX(0);
  transition: opacity ${timings.quick} ${cubicBezier},
    transform ${timings.medium} ${cubicBezier};
`
const Container = styled.div<{ isOpen?: boolean }>`
  ${({ isOpen }) => css`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    flex-direction: column;
    display: flex;
    opacity: 0;
    overflow: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    transform: translateX(100%);
    transition: opacity ${timings.medium} ${cubicBezier},
      transform ${timings.quick} ${cubicBezier};
    will-change: opacity, transform;
    width: calc(100% - 40px);
    background-color: ${colors.snow};
    cursor: default;

    ${up("laptop")} {
      display: none;
    }

    ${isOpen ? openProps : ""}
  `}
`

const ModuleToggle = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;
  margin: 0 16px 4px;
  border-bottom: 1px solid ${colors.divider};

  ${down("mobileLarge")} {
    margin: 0 10px 4px;
    grid-template-columns: 0.8fr 1fr 1.2fr;
  }

  span {
    display: none;
  }

  a {
    transition: 0.3s all ease;
    color: #aaa;
    white-space: normal;
    text-align: center;
    line-height: 1.15;
    display: block;
    letter-spacing: 0.02em;

    &.active,
    &:hover {
      font-weight: 600;

      &.automotive {
        color: ${colors.red};
      }

      &.powersports {
        color: ${colors.alt};
      }

      &.new-vehicle {
        color: ${colors.yellow};
      }
    }
  }
`

const activeProps = css`
  color: ${colors.snow};
  transition: 0.6s ease-out;
`

const Button = styled.div<{ active }>`
  ${({ active }) => css`
    font-family: "Jost", sans-serif;
    text-transform: uppercase;
    font-weight: 600;
    font-size: 14px;
    align-items: center;
    display: flex;
    height: 100%;
    margin: 0;
    padding: 0;
    outline: none;
    background: none;
    color: ${colors.snow};
    padding: 0 8px 12px;
    min-width: 0;
    justify-content: center;
    min-height: 52px;
    border-bottom: 2px solid transparent;

    &:not(:first-child) {
      border-left: 1px solid ${colors.divider};
    }

    ${down("tablet")} {
      font-size: 15px;
      min-height: 48px;
      padding: 0 6px 12px;
    }

    ${down("mobileLarge")} {
      font-size: 11px;
      min-height: 44px;
      padding: 0 2px 10px;
    }

    ${active ? activeProps : ""}

    &:focus,
    &:active {
      outline: none;
    }

    &:hover {
      cursor: pointer;
    }
  `}
`

interface MenuMobileProps {
  isOpen?: boolean
  data: {
    proSeriesHeader: string
    proSeriesBody: string
    proSeriesCategories: Array<{
      name: string
      slug: string
      powersportsSeries?: string
    }>
    mainSectionLabel: string
    mainSectionLinks: Array<{
      label: string
      link: string
    }>
    sideSectionLabel: string
    sideSectionLinks: Array<{
      label: string
      link: string
    }>
    footerRightColumnLabel: string
    footerRightColumnLinks: Array<{
      label: string
      link: string
    }>
    footerLeftColumnLabel: string
    footerLeftColumnLinks: Array<{
      label: string
      link: string
    }>
    cars: Array<{
      make: {
        name: string
        slug: string
      }
      model: {
        name: string
        slug: string
        image: {
          responsiveImage: DatoResponsiveImage
        }
        imageWidth?: number
        imageHeight?: number
        mobileImageWidth?: number
        mobileImageHeight?: number
      }
      trim?: {
        name: string
        slug: string
        image: {
          responsiveImage: DatoResponsiveImage
        }
        imageWidth?: number
        imageHeight?: number
        mobileImageWidth?: number
        mobileImageHeight?: number
      }
      years: string
      module: string
    }>
    makeModelOrder: Array<{
      make: { name: string; slug: string }
      models: Array<{ name: string; slug: string }>
    }>
    powersportsSeatCategories: Array<{
      name: string
      slug: string
      powersportsSeries?: string
    }>
    powersportsArmorCategories: Array<{
      name: string
      slug: string
      powersportsSeries?: string
    }>
  }
  menuStateOnOpen: MenuStates | undefined
  onRequestClose: () => void
  module?: string
}

export enum MenuStates {
  Picker = "picker",
  ProSeries = "proSeries",
  Products = "products",
  Explore = "explore",
  Support = "support",
}

const MenuMobile: React.FC<MenuMobileProps> = ({
  isOpen,
  data,
  menuStateOnOpen,
  onRequestClose,
  module = "automotive",
}) => {
  const { asPath } = useRouter()
  const isLateModelPage = asPath === "/late-model"
  const [menuState, setMenuState] = React.useState<MenuStates | undefined>(
    undefined
  )

  const clearMenuState = () => {
    setMenuState(undefined)
  }

  React.useEffect(() => {
    if (!isOpen) {
      clearMenuState()
    } else {
      if (!!menuStateOnOpen) {
        setMenuState(menuStateOnOpen)
      }
    }
  }, [isOpen])

  const menuRef: any = React.useRef(null)

  useModalBackgroundClose({
    modalRef: menuRef,
    isOpen,
    onRequestClose,
  })

  // console.log(data)

  return (
    <MobileOnly>
      <Backdrop isOpen={isOpen}>
        <Container id="mobileMenu" ref={menuRef} isOpen={isOpen}>
          <Header onRequestClose={onRequestClose} />
          <VerticalSpace height={2} />
          <ModuleToggle>
            <Button onClick={() => {}} active={false}>
              <Link
                className={
                  module == "automotive" && !isLateModelPage
                    ? "active automotive"
                    : "automotive"
                }
                href="/"
                prefetch={false}
              >
                Restomod
              </Link>
            </Button>
            <span>/</span>
            <Button onClick={() => {}} active={false}>
              <Link
                className={
                  isLateModelPage ? "active new-vehicle" : "new-vehicle"
                }
                href="/late-model"
                prefetch={false}
              >
                New Vehicle
              </Link>
            </Button>
            <span>/</span>
            <Button onClick={() => {}} active={false}>
              <Link
                className={
                  module == "powersports" ? "active powersports" : "powersports"
                }
                href="/powersports/"
                prefetch={false}
              >
                Powersports
              </Link>
            </Button>
          </ModuleToggle>
          <VerticalSpace height={2} />
          <Item
            header
            label="Shop by Vehicle"
            onClick={() => {
              setMenuState(MenuStates.Picker)
            }}
          />
          {module == "automotive" ? (
            <>
              <Item
                header
                label="Universal"
                onClick={() => {
                  setMenuState(MenuStates.ProSeries)
                }}
              />
              <Item
                header
                label="Configurator"
                onClick={() => {}}
                link={"/configurator"}
              />
              <Item
                header
                label="Become a Dealer"
                onClick={() => {}}
                link={"/become-a-dealer"}
              />
            </>
          ) : (
            <>
              <Item
                header
                label="Products"
                onClick={() => {
                  setMenuState(MenuStates.ProSeries)
                }}
              />
            </>
          )}
          <VerticalSpace height={40} />
          {module == "automotive" ? (
            <Item
              label="All Products"
              onClick={() => {
                setMenuState(MenuStates.Products)
              }}
            />
          ) : (
            ""
          )}
          <Item
            label="Explore"
            onClick={() => {
              setMenuState(MenuStates.Explore)
            }}
          />
          <Item
            label="Support"
            onClick={() => {
              setMenuState(MenuStates.Support)
            }}
          />
          <Footer onRequestClose={onRequestClose} />
          {module === "powersports" ? (
            <PowersportsCarSelector
              isOpen={menuState === MenuStates.Picker}
              cars={data.cars}
              makeModelOrder={data.makeModelOrder}
              onRequestClose={clearMenuState}
              onRequestCloseMenu={onRequestClose}
              module={module}
            />
          ) : (
            <CarSelector
              isOpen={menuState === MenuStates.Picker}
              cars={data.cars}
              module={module}
              makeModelOrder={data.makeModelOrder}
              onRequestClose={clearMenuState}
              onRequestCloseMenu={onRequestClose}
            />
          )}
          <ProSeries
            isOpen={menuState === MenuStates.ProSeries}
            header={
              module == "automotive"
                ? data.proSeriesHeader
                : "Powersports Products"
            }
            body={data.proSeriesBody}
            items={
              module == "automotive"
                ? data.proSeriesCategories
                : [
                    ...data.powersportsSeatCategories,
                    ...data.powersportsArmorCategories,
                  ]
            }
            onRequestBack={clearMenuState}
            onRequestClose={onRequestClose}
            module={module}
          />
          <Products
            isOpen={menuState === MenuStates.Products}
            mainSectionLabel={data.mainSectionLabel}
            mainSectionLinks={data.mainSectionLinks}
            sideSectionLabel={data.sideSectionLabel}
            sideSectionLinks={data.sideSectionLinks}
            onRequestBack={clearMenuState}
            onRequestClose={onRequestClose}
          />
          <SubMenu
            isOpen={menuState === MenuStates.Explore}
            label={data.footerRightColumnLabel}
            items={data.footerRightColumnLinks}
            onRequestBack={clearMenuState}
            onRequestClose={onRequestClose}
          />
          <SubMenu
            isOpen={menuState === MenuStates.Support}
            label={data.footerLeftColumnLabel}
            items={data.footerLeftColumnLinks}
            onRequestBack={clearMenuState}
            onRequestClose={onRequestClose}
          />
        </Container>
      </Backdrop>
    </MobileOnly>
  )
}

export default MenuMobile
